import React, { useEffect, useRef, useState } from "react";
import { format, isToday, isTomorrow } from "date-fns";
import { pl } from "date-fns/locale";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTranslation } from "react-i18next";
import EAButton from "../Buttons/EAButton";
import EAIconButton from "../Buttons/EAIconButton";
import LoadableComponent from "../../../../features/Loading/LoadableComponent";
import "./style.scss";

interface IProps {
  /** Dni jako yyyy-MM-dd. Rodzic decyduje, czy to poniedziałek–niedziela, czy inny zakres. */
  columns: string[];
  hours: string[];
  renderCell: (columnKey: string, hour: string) => React.ReactNode;
  /** Gdy podane, wiersz zostaje tylko jeśli któreś z widocznych dni ma w nim zajęcia. */
  occupiesSlot?: (columnKey: string, hour: string) => boolean;
  hasPastHours?: boolean;
  onShowPastHours?: () => void;
  /**
   * Godzina zakończona na danym dniu. Siatka używa tego tylko na mobile:
   * chowa takie wiersze oglądanego dnia i pokazuje przycisk, gdy jest co odkryć.
   * Desktop zawsze rysuje pełną listę godzin.
   */
  isPastHour?: (columnKey: string, hour: string) => boolean;
  loading?: boolean;
  isEmpty?: boolean;
  empty?: React.ReactNode;
  isColumnInRange?: (columnKey: string) => boolean;
  /** Widoczny dzień (yyyy-MM-dd). Razem z onShiftColumn steruje strzałkami także poza bieżącym tygodniem. */
  activeColumn?: string;
  onShiftColumn?: (direction: -1 | 1) => void;
  canShiftPrevious?: boolean;
  canShiftNext?: boolean;
}

const TIME_COLUMN_WIDTH = "72px";
const SCHEDULE_BREAKPOINT = "(max-width: 980px)";

const WeekHourGrid: React.FC<IProps> = ({
  columns,
  hours,
  renderCell,
  occupiesSlot,
  hasPastHours = false,
  onShowPastHours,
  isPastHour,
  loading = false,
  isEmpty = false,
  empty,
  isColumnInRange,
  activeColumn,
  onShiftColumn,
  canShiftPrevious = false,
  canShiftNext = false,
}) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery(SCHEDULE_BREAKPOINT);
  const [currentColumnIndex, setCurrentColumnIndex] = useState(0);
  const [revealPastHours, setRevealPastHours] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [isHeaderStuck, setIsHeaderStuck] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);
  const stickySentinelRef = useRef<HTMLDivElement>(null);

  const weekKey = columns[0] ?? "";

  useEffect(() => {
    const header = headerRef.current;
    if (!header) return;
    const measure = () => setHeaderHeight(header.getBoundingClientRect().height);
    measure();

    const resizeObserverCtor = (window as unknown as Record<string, unknown>).ResizeObserver as
      | (new (callback: () => void) => { observe: (target: Element) => void; disconnect: () => void })
      | undefined;

    if (!resizeObserverCtor) {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }

    const observer = new resizeObserverCtor(measure);
    observer.observe(header);
    return () => observer.disconnect();
  }, [isMobile, loading, isEmpty]);

  useEffect(() => {
    const sentinel = stickySentinelRef.current;
    const header = headerRef.current;
    if (!isMobile || !sentinel || !header || typeof IntersectionObserver === "undefined") {
      setIsHeaderStuck(false);
      return;
    }
    const stickyTop = parseFloat(getComputedStyle(header).top) || 0;
    const observer = new IntersectionObserver(
      ([entry]) => setIsHeaderStuck(!entry.isIntersecting && entry.boundingClientRect.top < stickyTop),
      { rootMargin: `-${stickyTop}px 0px 0px 0px`, threshold: 0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [isMobile, loading, isEmpty]);

  useEffect(() => {
    if (activeColumn) {
      const activeIndex = columns.indexOf(activeColumn);
      if (activeIndex >= 0) setCurrentColumnIndex(activeIndex);
      return;
    }
    const todayKey = format(new Date(), "yyyy-MM-dd");
    const todayIndex = columns.indexOf(todayKey);
    setCurrentColumnIndex(todayIndex >= 0 ? todayIndex : 0);
    // columns poza weekKey zmieniają tożsamość przy każdym renderze
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekKey, activeColumn]);

  const isSingleColumn = columns.length === 1;
  const safeIndex = Math.min(currentColumnIndex, Math.max(columns.length - 1, 0));
  const displayedColumns =
    isMobile && columns.length > 1 ? [columns[safeIndex]] : columns;
  const canGoPrevious = onShiftColumn ? canShiftPrevious : safeIndex > 0;
  const canGoNext = onShiftColumn ? canShiftNext : safeIndex < columns.length - 1;

  const shiftColumn = (direction: -1 | 1) => {
    if (onShiftColumn) {
      onShiftColumn(direction);
      return;
    }
    setCurrentColumnIndex((index) => Math.min(Math.max(index + direction, 0), columns.length - 1));
  };
  const gridTemplateColumns = `${TIME_COLUMN_WIDTH} repeat(${displayedColumns.length}, minmax(160px, 1fr))`;
  const occupiedRows = occupiesSlot
    ? hours.filter((hour) =>
        displayedColumns.some((dayKey) => {
          const inRange = isColumnInRange ? isColumnInRange(dayKey) : true;
          return inRange && occupiesSlot(dayKey, hour);
        }),
      )
    : hours;

  const hidePastOnMobile = Boolean(isPastHour) && isMobile && columns.length > 1;
  const displayedDay = hidePastOnMobile ? displayedColumns[0] : undefined;

  useEffect(() => {
    setRevealPastHours(false);
  }, [weekKey, displayedDay]);

  const pastHours =
    hidePastOnMobile && displayedDay && isPastHour
      ? occupiedRows.filter((hour) => isPastHour(displayedDay, hour))
      : [];
  const visibleRows =
    hidePastOnMobile && !revealPastHours && displayedDay && isPastHour
      ? occupiedRows.filter((hour) => !isPastHour(displayedDay, hour))
      : occupiedRows;
  const showPastButton = isPastHour
    ? hidePastOnMobile && !revealPastHours && pastHours.length > 0
    : hasPastHours;

  const getDayName = (dateString: string) => {
    const date = new Date(`${dateString}T00:00:00`);
    if (isToday(date)) return t("dzisiaj");
    if (isTomorrow(date)) return t("jutro");
    const polishDay = format(date, "EEEE", { locale: pl });
    return t(polishDay.charAt(0).toUpperCase() + polishDay.slice(1));
  };

  const getDayHeader = (dateString: string) => `${getDayName(dateString)} ${format(new Date(`${dateString}T00:00:00`), "dd.MM")}`;

  const renderHeader = () => {
    if (isMobile && columns.length > 1) {
      const dayKey = columns[safeIndex];
      return (
        <div
          ref={headerRef}
          className={`week-hour-grid__header--mobile-nav${isHeaderStuck ? " week-hour-grid__header--stuck" : ""}`}
        >
          <div className="week-hour-grid__mobile-nav">
            <EAIconButton
              title={t("poprzedni_dzien", "Poprzedni dzień")}
              icon={<MdChevronLeft />}
              variant="icon"
              onClick={() => shiftColumn(-1)}
              disabled={!canGoPrevious}
            />
            <div className="week-hour-grid__mobile-nav-content">
              <span className="week-hour-grid__mobile-nav-name">{dayKey ? getDayName(dayKey) : ""}</span>
              <span className="week-hour-grid__mobile-nav-date">
                {dayKey ? format(new Date(`${dayKey}T00:00:00`), "dd.MM.yyyy") : ""}
              </span>
            </div>
            <EAIconButton
              title={t("nastepny_dzien", "Następny dzień")}
              icon={<MdChevronRight />}
              variant="icon"
              onClick={() => shiftColumn(1)}
              disabled={!canGoNext}
            />
          </div>
        </div>
      );
    }

    return (
      <div ref={headerRef} className="week-hour-grid__header" style={{ gridTemplateColumns }}>
        <div className="week-hour-grid__time-header">{t("godzina")}</div>
        {displayedColumns.map((dayKey) => {
          const dayDate = new Date(`${dayKey}T00:00:00`);
          const columnIsToday = isToday(dayDate);
          return (
            <div
              key={dayKey}
              className={`week-hour-grid__day-header${columnIsToday ? " week-hour-grid__day-header--today" : ""}`}
            >
              {columnIsToday && <span className="week-hour-grid__today-dot" aria-hidden="true" />}
              <span className="week-hour-grid__day-name">{getDayHeader(dayKey)}</span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <LoadableComponent loading={loading}>
      {isEmpty && !loading ? (
        <div className="week-hour-grid__empty">{empty}</div>
      ) : (
        <div
          className={`week-hour-grid${isMobile ? " week-hour-grid--mobile" : ""}`}
          style={{ "--week-hour-grid-header-height": `${headerHeight}px` } as React.CSSProperties}
        >
          <div ref={stickySentinelRef} className="week-hour-grid__sticky-sentinel" aria-hidden="true" />
          {renderHeader()}
          <div className="week-hour-grid__separator" />
          {showPastButton && (
            <div className="week-hour-grid__past-hours-row">
              <EAButton
                title={t("pokaz_wczesniejsze_godziny", "Pokaż wcześniejsze godziny")}
                onClick={isPastHour ? () => setRevealPastHours(true) : onShowPastHours}
                variant="caption"
                color="dark"
                compact
              />
            </div>
          )}
          <div className="week-hour-grid__body">
            {visibleRows.map((hour) => (
              <div key={hour} className="week-hour-grid__row" style={isMobile ? undefined : { gridTemplateColumns }}>
                {isMobile ? (
                  <div className="week-hour-grid__hour-section">
                    <p className="week-hour-grid__time-text">{hour}</p>
                  </div>
                ) : (
                  <div className="week-hour-grid__time-cell">
                    <p className="week-hour-grid__time-text">{hour}</p>
                  </div>
                )}
                {displayedColumns.map((dayKey) => {
                  const inRange = isColumnInRange ? isColumnInRange(dayKey) : true;
                  return (
                    <div
                      key={`${dayKey}-${hour}`}
                      className={`week-hour-grid__slot-cell${isSingleColumn ? " week-hour-grid__slot-cell--single-column" : ""}${!inRange ? " week-hour-grid__slot-cell--out-of-range" : ""}`}
                    >
                      {inRange ? renderCell(dayKey, hour) : null}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}
    </LoadableComponent>
  );
};

export default WeekHourGrid;
